import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer } from '@helios/ui';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { qHomeAlertsDetail, type HomeAlertsDetailRow } from '@helios/data-access';
import {
  DrawerSummaryBanner,
  DrawerRowCard,
  DrawerFooterButton,
  DrawerEmptyState,
  PatientAvatar,
  StatusBadge,
  fmtDrawerRelative,
} from './shared';

export interface PatientAlertsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alertCounts?: { critical: number; warning: number };
  portalStyle?: CSSProperties;
}

export function PatientAlertsDrawer({
  open,
  onOpenChange,
  alertCounts,
  portalStyle,
}: PatientAlertsDrawerProps) {
  const navigate = useNavigate();
  const { data: rows = [] } = usePowerSyncQuery<HomeAlertsDetailRow>(qHomeAlertsDetail());

  const critical = alertCounts?.critical ?? rows.filter((r) => r.alert_level === 'critical').length;
  const warning = alertCounts?.warning ?? rows.filter((r) => r.alert_level === 'warning').length;

  const alertBorderColor = (level: string) => {
    if (level === 'critical') return '#fecaca';
    if (level === 'warning') return '#fed7aa';
    return 'var(--border, #e5e7eb)';
  };

  const alertLabel = (level: string) => {
    if (level === 'critical') return 'Critical';
    if (level === 'warning') return 'Warning';
    return 'Info';
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Patient Alerts"
      description="Active patient alerts"
      width={520}
      portalStyle={portalStyle}
      footer={
        <DrawerFooterButton
          label="View All Alerts"
          color="#db2777"
          onClick={() => {
            navigate('/patients');
            onOpenChange(false);
          }}
        />
      }
    >
      <DrawerSummaryBanner
        icon="alert-triangle"
        iconBg="#fef2f2"
        iconColor="#dc2626"
        value={String(rows.length)}
        description={`${critical} critical • ${warning} warning`}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3, 12px)',
          padding: 'var(--sp-4, 16px)',
        }}
      >
        {rows.length === 0 && <DrawerEmptyState message="No active patient alerts" />}

        {rows.map((row) => (
          <DrawerRowCard key={row.note_id} borderColor={alertBorderColor(row.alert_level)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
              <PatientAvatar name={row.patient_name} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm, 13px)',
                    fontWeight: 'var(--font-weight-semi, 600)',
                    color: 'var(--text, #111827)',
                  }}
                >
                  {row.patient_name ?? 'Unknown'}
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs, 12px)',
                    color: 'var(--text-muted, #6b7280)',
                  }}
                >
                  {fmtDrawerRelative(row.created_at)}
                </div>
              </div>
              <StatusBadge label={alertLabel(row.alert_level)} />
            </div>

            <div
              style={{
                fontSize: 'var(--font-size-sm, 13px)',
                color: 'var(--text, #111827)',
                lineHeight: 1.4,
              }}
            >
              {row.title ?? row.body ?? '—'}
            </div>

            <button
              onClick={() => {
                navigate(`/patient/${row.patient_id}/encounters`);
                onOpenChange(false);
              }}
              style={{
                width: '100%',
                height: 32,
                borderRadius: 'var(--radius-md, 10px)',
                border: 'none',
                background: row.alert_level === 'critical' ? '#ea580c' : '#d97706',
                color: '#fff',
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 'var(--font-weight-semi, 600)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
              }}
            >
              Open Patient →
            </button>
          </DrawerRowCard>
        ))}
      </div>
    </Drawer>
  );
}
